import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Tooltip,
  Collapse,
  useMediaQuery,
  useTheme,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockIcon from '@mui/icons-material/Lock';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SecurityIcon from '@mui/icons-material/Security';
import PersonIcon from '@mui/icons-material/Person';
import salesService, {
  QuickSaleCashierDto,
  CreateQuickSaleCashierDto,
  UpdateQuickSaleCashierDto,
} from '../../../../services/salesService';
import { useTranslation } from 'react-i18next';

interface CashierSettingsTabProps {
  screenId: number;
  screenType: 'QuickSale' | 'Restaurant' | 'Pyk';
  companyId: string;
}

// Yetki tanımı tipi
interface PermissionDefinition {
  key: string;
  label: string;
  color: string;
  indent?: boolean;
  dependsOn?: string;
}

// Normal Hızlı Satış Yetkileri
const QUICKSALE_PERMISSIONS: PermissionDefinition[] = [
  { key: 'CanChangePrice', label: 'Fiyat Değiştirme', color: 'primary' },
  { key: 'CanApplyDiscount', label: 'İndirim Uygulama', color: 'primary' },
  { key: 'CanCancelSale', label: 'Satış İptal', color: 'warning' },
  { key: 'CanViewReports', label: 'Rapor Görüntüleme', color: 'primary' },
  { key: 'CanViewEndOfDay', label: 'Gün Sonu Yazdırma', color: 'primary' },
  { key: 'CanWarehouseOperations', label: 'Depo İşlemleri', color: 'primary' },
  { key: 'CanViewAccountBalance', label: 'Cari Bakiye Görme', color: 'primary' },
  { key: 'CanListAccounts', label: 'Cari Listeleme', color: 'primary' },
  { key: 'CanQuickAddItem', label: 'Hızlı Stok Kaydı', color: 'primary' },
  { key: 'CanAddAccount', label: 'Cari Ekleme', color: 'primary' },
  { key: 'CanPosRefund', label: 'POS İade', color: 'error' }, // ✅ YENİ: POS İade yetkisi
  { key: 'CanClearCart', label: 'Sepeti Temizle', color: 'warning' }, // ✅ YENİ: Sepeti Temizle yetkisi
  { key: 'CanCollectAccountPayment', label: 'Cari Tahsilat Yapabilsin', color: 'success' }, // ✅ YENİ: Cari tahsilat yetkisi
];

// Restoran Yetkileri (Garson yetkilerine benzer)
const RESTAURANT_PERMISSIONS: PermissionDefinition[] = [
  { key: 'CanCloseSale', label: 'Hesap Kapatma (Ödeme)', color: 'primary' },
  { key: 'CanCloseTable', label: 'Masa Kapatma (Vazgeçme)', color: 'warning' },
  { key: 'CanApplyDiscount', label: 'İndirim Uygulama', color: 'primary' },
  { key: 'CanAddProduct', label: 'Ürün Ekleme', color: 'primary' },
  { key: 'CanRemoveProduct', label: 'Ürün Silme', color: 'primary' },
  { key: 'CanDeletePrintedItems', label: '↳ Yazdırılmış Ürün Silme', color: 'warning', indent: true, dependsOn: 'CanRemoveProduct' },
  { key: 'CanDeleteUnprintedItems', label: 'Yazdırılmamış Ürün Silme (mutfağa gitmemiş)', color: 'warning' },
  { key: 'CanClearCart', label: 'Sepeti Temizleme', color: 'primary' },
  { key: 'CanPrintToKitchen', label: 'Mutfağa Yazdırma', color: 'primary' },
  { key: 'CanTransferTable', label: 'Masa Taşıma', color: 'secondary' },
  { key: 'CanPosRefund', label: 'POS İade', color: 'error' },
  { key: 'CanCancelReceipt', label: 'Adisyon İptal', color: 'error' },
  { key: 'CanCancelUnprintedReceipt', label: '↳ Yazdırılmamış Adisyon İptal (mutfağa gitmemiş)', color: 'error', indent: true },
  { key: 'CanPrintReceipt', label: 'Adisyon Yazdırma', color: 'info' },
  { key: 'CanAddProductAfterReceiptPrint', label: 'Adisyon Sonrası Ürün Ekleme', color: 'warning' },
  { key: 'CanUsePavoP2P', label: 'PavoPay P2P Kullanabilsin', color: 'secondary' },
  { key: 'CanViewEndOfDay', label: 'Gün Sonu Yazdırma', color: 'primary' },
  { key: 'CanCollectAccountPayment', label: 'Cari Tahsilat Yapabilsin', color: 'success' }, // ✅ YENİ: Cari tahsilat yetkisi
];

// PYK (Ön Ödemeli Kart) Yetkileri
const PYK_PERMISSIONS: PermissionDefinition[] = [
  { key: 'CanPykLoadBalance', label: 'Bakiye Yükleme', color: 'primary' },
  { key: 'CanPykRefundBalance', label: 'Bakiye İade', color: 'error' },
  { key: 'CanPykManageSession', label: 'Oturum Başlatma / Kapatma', color: 'warning' },
];

const CashierSettingsTab: React.FC<CashierSettingsTabProps> = ({
  screenId,
  screenType,
  companyId,
}) => {
  const { t } = useTranslation('salesSystem');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [cashiers, setCashiers] = useState<QuickSaleCashierDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showPermissions, setShowPermissions] = useState(false);

  // Form state
  const [isEditing, setIsEditing] = useState(false);
  const [editingCashierId, setEditingCashierId] = useState<number | null>(null);
  const [cashierName, setCashierName] = useState('');
  const [cashierPin, setCashierPin] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [showPin, setShowPin] = useState(false);

  // Yetkiler (dinamik - screenType'a göre)
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});

  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [cashierToDelete, setCashierToDelete] = useState<QuickSaleCashierDto | null>(null);

  // Hangi yetkiler gösterilecek
  const activePermissions =
    screenType === 'Restaurant' ? RESTAURANT_PERMISSIONS
      : screenType === 'Pyk' ? PYK_PERMISSIONS
        : QUICKSALE_PERMISSIONS;

  // Kasiyerleri yükle
  const loadCashiers = async () => {
    try {
      setLoading(true);
      setError(null);

      let data: QuickSaleCashierDto[] = [];
      if (screenType === 'QuickSale') {
        data = await salesService.getCashiersByQuickSaleScreenId(screenId);
      } else if (screenType === 'Pyk') {
        data = await salesService.getCashiersByPykScreenId(screenId);
      } else {
        data = await salesService.getCashiersByRestaurantScreenId(screenId);
      }

      // DEBUG LOG - API'den gelen kasiyerler
      console.log('📥 LOAD CASHIERS - API\'den gelen:', data.map(c => ({
        Id: c.Id,
        Name: c.CashierName,
        CanCancelReceipt: c.CanCancelReceipt,
        CanPrintReceipt: c.CanPrintReceipt,
        CanClearCart: c.CanClearCart
      })));

      setCashiers(data);
    } catch (err: any) {
      console.error('Kasiyerler yüklenirken hata:', err);
      setError(t('cashierSettingsTab.errors.loadFailed', { message: err.response?.data?.Message || err.message }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCashiers();
  }, [screenId, screenType]);

  // Form temizle ve varsayılan yetkileri ayarla
  const resetForm = () => {
    setIsEditing(false);
    setEditingCashierId(null);
    setCashierName('');
    setCashierPin('');
    setIsActive(true);
    setShowPin(false);
    setError(null);
    setSuccessMessage(null);

    // Varsayılan yetkiler
    const defaultPermissions: Record<string, boolean> = {};
    activePermissions.forEach((perm) => {
      if (perm.key === 'CanDeletePrintedItems' || perm.key === 'CanDeleteUnprintedItems') {
        defaultPermissions[perm.key] = false;
      } else if (perm.key === 'CanAddProductAfterReceiptPrint') {
        defaultPermissions[perm.key] = false;
      } else if (perm.key === 'CanCancelReceipt' || perm.key === 'CanCancelUnprintedReceipt' || perm.key === 'CanPrintReceipt') {
        defaultPermissions[perm.key] = false;
      } else if (perm.key === 'CanCancelSale' || perm.key === 'CanCloseTable') {
        defaultPermissions[perm.key] = true;
      } else {
        defaultPermissions[perm.key] = true;
      }
    });
    setPermissions(defaultPermissions);
  };

  useEffect(() => {
    resetForm();
  }, [screenType]);

  // Yeni kasiyer ekle
  const handleAdd = async () => {
    try {
      setError(null);
      setSuccessMessage(null);

      if (!cashierName.trim()) {
        setError(t('cashierSettingsTab.errors.nameRequired'));
        return;
      }

      if (cashierPin.length < 4) {
        setError(t('cashierSettingsTab.errors.pinLength'));
        return;
      }

      const newCashier: CreateQuickSaleCashierDto = {
        ScreenType: screenType,
        QuickSaleScreenId: screenType === 'QuickSale' ? screenId : undefined,
        RestaurantScreenId: screenType === 'Restaurant' ? screenId : undefined,
        PykScreenId: screenType === 'Pyk' ? screenId : undefined,
        CashierName: cashierName.trim(),
        CashierPin: cashierPin,
        IsActive: isActive,
        // Yetkiler
        ...permissions,
      };

      // DEBUG LOG - API'ye gönderilen veri
      console.log('📤 CREATE CASHIER - Gönderilen:', {
        permissions,
        CanCancelReceipt: newCashier.CanCancelReceipt,
        CanPrintReceipt: newCashier.CanPrintReceipt,
        CanClearCart: newCashier.CanClearCart,
        fullData: newCashier
      });

      await salesService.createCashier(newCashier);
      setSuccessMessage(t('cashierSettingsTab.messages.added'));
      await loadCashiers();
      resetForm();
    } catch (err: any) {
      console.error('Kasiyer eklenirken hata:', err);
      setError(t('cashierSettingsTab.errors.addFailed', { message: err.response?.data?.Message || err.message }));
    }
  };

  // Kasiyer düzenle
  const handleEdit = (cashier: QuickSaleCashierDto) => {
    // DEBUG LOG - DB'den gelen kasiyer verisi
    console.log('📥 EDIT CASHIER - DB\'den gelen:', {
      Id: cashier.Id,
      Name: cashier.CashierName,
      CanCancelReceipt: cashier.CanCancelReceipt,
      CanPrintReceipt: cashier.CanPrintReceipt,
      CanClearCart: cashier.CanClearCart,
      fullData: cashier
    });

    setIsEditing(true);
    setEditingCashierId(cashier.Id);
    setCashierName(cashier.CashierName);
    setCashierPin(cashier.CashierPin);
    setIsActive(cashier.IsActive);
    setError(null);
    setSuccessMessage(null);

    // Yetkileri yükle
    const loadedPermissions: Record<string, boolean> = {};
    activePermissions.forEach((perm) => {
      const raw = (cashier as any)[perm.key];
      if (perm.key === 'CanAddProductAfterReceiptPrint' || perm.key === 'CanCancelReceipt' || perm.key === 'CanCancelUnprintedReceipt' || perm.key === 'CanPrintReceipt' || perm.key === 'CanDeletePrintedItems' || perm.key === 'CanDeleteUnprintedItems') {
        loadedPermissions[perm.key] = raw ?? false;
      } else {
        loadedPermissions[perm.key] = raw ?? true;
      }
    });
    
    // DEBUG LOG - Yüklenen yetkiler
    console.log('📥 EDIT CASHIER - Yüklenen yetkiler:', loadedPermissions);
    
    setPermissions(loadedPermissions);
  };

  // Kasiyer güncelle
  const handleUpdate = async () => {
    try {
      setError(null);
      setSuccessMessage(null);

      if (!editingCashierId) return;

      if (!cashierName.trim()) {
        setError(t('cashierSettingsTab.errors.nameRequired'));
        return;
      }

      if (cashierPin.length < 4) {
        setError(t('cashierSettingsTab.errors.pinLength'));
        return;
      }

      const updateData: UpdateQuickSaleCashierDto = {
        Id: editingCashierId,
        CashierName: cashierName.trim(),
        CashierPin: cashierPin,
        IsActive: isActive,
        // Yetkiler
        ...permissions,
      };

      // DEBUG LOG - API'ye gönderilen veri
      console.log('📤 UPDATE CASHIER - Gönderilen:', {
        permissions,
        CanCancelReceipt: updateData.CanCancelReceipt,
        CanPrintReceipt: updateData.CanPrintReceipt,
        CanClearCart: updateData.CanClearCart,
        fullData: updateData
      });

      await salesService.updateCashier(editingCashierId, updateData);
      setSuccessMessage(t('cashierSettingsTab.messages.updated'));
      await loadCashiers();
      resetForm();
    } catch (err: any) {
      console.error('Kasiyer güncellenirken hata:', err);
      setError(t('cashierSettingsTab.errors.updateFailed', { message: err.response?.data?.Message || err.message }));
    }
  };

  // Silme modalı aç
  const handleDeleteClick = (cashier: QuickSaleCashierDto) => {
    setCashierToDelete(cashier);
    setDeleteConfirmOpen(true);
  };

  // Kasiyer sil
  const handleDeleteConfirm = async () => {
    if (!cashierToDelete) return;

    try {
      setError(null);
      await salesService.deleteCashier(cashierToDelete.Id);
      setSuccessMessage(t('cashierSettingsTab.messages.deleted'));
      await loadCashiers();
      setDeleteConfirmOpen(false);
      setCashierToDelete(null);
      resetForm();
    } catch (err: any) {
      console.error('Kasiyer silinirken hata:', err);
      setError(t('cashierSettingsTab.errors.deleteFailed', { message: err.response?.data?.Message || err.message }));
      setDeleteConfirmOpen(false);
    }
  };

  // Yetki değiştir
  const handlePermissionChange = (key: string, value: boolean) => {
    setPermissions((prev) => {
      const updated = { ...prev, [key]: value };

      // CanRemoveProduct kapatılırsa CanDeletePrintedItems da kapatılmalı
      if (key === 'CanRemoveProduct' && !value) {
        updated.CanDeletePrintedItems = false;
      }

      return updated;
    });
  };

  // Yetkileri göster (kısa format)
  const getPermissionSummary = (cashier: QuickSaleCashierDto) => {
    const summary: string[] = [];

    if (screenType === 'Restaurant') {
      if (cashier.CanCloseSale) summary.push(`💰${t('cashierSettingsTab.permissionSummary.account')}`);
      if (cashier.CanCloseTable) summary.push(`🚪${t('cashierSettingsTab.permissionSummary.table')}`);
      if (cashier.CanAddProduct) summary.push(`➕${t('cashierSettingsTab.permissionSummary.add')}`);
      if (cashier.CanRemoveProduct) summary.push(`➖${t('cashierSettingsTab.permissionSummary.remove')}`);
      if (cashier.CanClearCart) summary.push(`🗑️${t('cashierSettingsTab.permissionSummary.clear')}`);
      if (cashier.CanPrintToKitchen) summary.push(`🖨️${t('cashierSettingsTab.permissionSummary.print')}`);
      if (cashier.CanDeletePrintedItems) summary.push(`⚠️${t('cashierSettingsTab.permissionSummary.deletePrinted')}`);
      if (cashier.CanTransferTable) summary.push(`🔄${t('cashierSettingsTab.permissionSummary.transfer')}`);
      if (cashier.CanViewEndOfDay) summary.push(`📅${t('cashierSettingsTab.permissionSummary.endOfDay')}`);
    } else if (screenType === 'Pyk') {
      if (cashier.CanPykLoadBalance) summary.push(`⬆️${t('cashierSettingsTab.permissionSummary.loadBalance')}`);
      if (cashier.CanPykRefundBalance) summary.push(`⬇️${t('cashierSettingsTab.permissionSummary.refundBalance')}`);
      if (cashier.CanPykManageSession) summary.push(`🔑${t('cashierSettingsTab.permissionSummary.session')}`);
    } else {
      if (cashier.CanChangePrice) summary.push(`💵${t('cashierSettingsTab.permissionSummary.price')}`);
      if (cashier.CanApplyDiscount) summary.push(`🏷️${t('cashierSettingsTab.permissionSummary.discount')}`);
      if (cashier.CanCancelSale) summary.push(`❌${t('cashierSettingsTab.permissionSummary.cancel')}`);
      if (cashier.CanViewReports) summary.push(`📊${t('cashierSettingsTab.permissionSummary.report')}`);
      if (cashier.CanViewEndOfDay) summary.push(`📅${t('cashierSettingsTab.permissionSummary.endOfDay')}`);
      if (cashier.CanWarehouseOperations) summary.push(`📦${t('cashierSettingsTab.permissionSummary.warehouse')}`);
      if (cashier.CanViewAccountBalance) summary.push(`💳${t('cashierSettingsTab.permissionSummary.balance')}`);
      if (cashier.CanListAccounts) summary.push(`📋${t('cashierSettingsTab.permissionSummary.accounts')}`);
      if (cashier.CanQuickAddItem) summary.push(`⚡${t('cashierSettingsTab.permissionSummary.stock')}`);
      if (cashier.CanAddAccount) summary.push(`➕${t('cashierSettingsTab.permissionSummary.addAccount')}`);
    }

    return summary.length > 0 ? summary.join(' • ') : t('cashierSettingsTab.permissionSummary.none');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, height: '100%', p: 2 }}>
      {/* Sol/Üst Panel - Form */}
      <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', flex: 1, maxHeight: { xs: 'none', md: 500 }, overflowY: { xs: 'visible', md: 'auto' } }}>
        <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
          <PersonIcon fontSize="small" color={screenType === 'Restaurant' ? 'warning' : 'primary'} />
          {isEditing ? t('cashierSettingsTab.form.editTitle') : t('cashierSettingsTab.form.newTitle')}
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              label={t('cashierSettingsTab.form.name')}
              variant="outlined"
              size="small"
              value={cashierName}
              onChange={(e) => setCashierName(e.target.value)}
              placeholder={t('cashierSettingsTab.form.namePlaceholder')}
              sx={{ flex: 1 }}
              fullWidth={isMobile}
            />

            <TextField
              label={t('cashierSettingsTab.form.pin')}
              variant="outlined"
              size="small"
              type={showPin ? 'text' : 'password'}
              value={cashierPin}
              onChange={(e) => setCashierPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder={t('cashierSettingsTab.form.pinPlaceholder')}
              sx={{ width: { xs: '100%', sm: '120px' } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPin(!showPin)} edge="end" size="small">
                      {showPin ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Yetkiler Butonu */}
          <Button
            variant="outlined"
            size="small"
            onClick={() => setShowPermissions(!showPermissions)}
            startIcon={<SecurityIcon />}
            endIcon={showPermissions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ alignSelf: 'flex-start' }}
            color={screenType === 'Restaurant' ? 'warning' : 'primary'}
          >
            {t('cashierSettingsTab.form.permissions')} {showPermissions ? t('cashierSettingsTab.form.hide') : t('cashierSettingsTab.form.show')}
          </Button>

          {/* Yetkiler - Collapse */}
          <Collapse in={showPermissions}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
              {activePermissions.map((perm) => {
                const isDisabled = perm.dependsOn ? !permissions[perm.dependsOn] : false;

                return (
                  <FormControlLabel
                    key={perm.key}
                    control={
                      <Switch
                        checked={permissions[perm.key] ?? false}
                        onChange={(e) => handlePermissionChange(perm.key, e.target.checked)}
                        color={perm.color as any}
                        size="small"
                        disabled={isDisabled}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        color={isDisabled ? 'text.disabled' : 'text.secondary'}
                      >
                        {t(`cashierSettingsTab.permissions.${perm.key}`)}
                      </Typography>
                    }
                    sx={{ m: 0, ml: perm.indent ? 2 : 0 }}
                  />
                );
              })}
            </Box>
          </Collapse>

          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={<Typography variant="body2">{t('cashierSettingsTab.form.active')}</Typography>}
            sx={{ mt: 0.5, m: 0 }}
          />

          <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
            {isEditing ? (
              <>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={handleUpdate} fullWidth>
                  {t('cashierSettingsTab.form.update')}
                </Button>
                <Button variant="outlined" startIcon={<CancelIcon />} onClick={resetForm} fullWidth>
                  {t('cashierSettingsTab.common.cancel')}
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAdd}
                fullWidth
                color={screenType === 'Restaurant' ? 'warning' : 'primary'}
              >
                {t('cashierSettingsTab.form.add')}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Sağ/Alt Panel - Liste */}
      <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', flex: 1, display: 'flex', flexDirection: 'column', maxHeight: { xs: 'none', md: 500 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            {t('cashierSettingsTab.list.title', { count: cashiers.length })}
          </Typography>
          <Chip
            label={screenType === 'Restaurant' ? t('restaurantScreen.restaurant') : t('quickSaleSettingsModal.titles.quickSale')}
            size="small"
            color={screenType === 'Restaurant' ? 'warning' : 'primary'}
          />
        </Box>
        <Divider sx={{ mb: 1 }} />

        {cashiers.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <PersonIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {t('cashierSettingsTab.list.empty')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('cashierSettingsTab.list.emptyHint')}
            </Typography>
          </Box>
        ) : (
          <List dense sx={{ overflowY: { xs: 'visible', md: 'auto' }, flexGrow: 1, maxHeight: { xs: 'none', md: 400 } }}>
            {cashiers.map((cashier) => (
              <ListItem
                key={cashier.Id}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  mb: 1,
                  bgcolor: !cashier.IsActive ? '#f5f5f5' : 'white',
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {cashier.CashierName}
                      </Typography>
                      {!cashier.IsActive && (
                        <Typography variant="caption" color="error">
                          ({t('cashierSettingsTab.form.passive')})
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.5 }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                        🔒 PIN: {cashier.CashierPin}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', lineHeight: 1.2 }}>
                        {getPermissionSummary(cashier)}
                      </Typography>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Tooltip title={t('cashierSettingsTab.actions.edit')}>
                    <IconButton edge="end" size="small" onClick={() => handleEdit(cashier)} sx={{ mr: 0.5 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('cashierSettingsTab.actions.delete')}>
                    <IconButton edge="end" size="small" color="error" onClick={() => handleDeleteClick(cashier)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {/* Silme Onay Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>{t('cashierSettingsTab.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('cashierSettingsTab.deleteDialog.description', { name: cashierToDelete?.CashierName })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>{t('cashierSettingsTab.common.cancel')}</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            {t('cashierSettingsTab.actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CashierSettingsTab;
